import { useCreateAiWorkersMutation, RouteState, useUpdateAiWorkersMutation, useTypedSelector, useGetDifyWorkflowsQuery, useGetCredentialsListQuery } from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast } from '@circuitry-ai/doc-ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, SelectItem, Textarea } from '@nextui-org/react';

const LABEL_CLASS = 'text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal';
const DESC_CLASS = 'font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]';

interface FormRowProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  description?: string;
  children: ReactNode;
}

const FormRow = ({ label, htmlFor, required, description, children }: FormRowProps) => (
  <div className="flex flex-row gap-3">
    <label htmlFor={htmlFor} className="flex flex-col w-[30%]">
      <span className={LABEL_CLASS}>
        {label}{required && <span className="text-danger">*</span>}
      </span>
      {description && <div className={DESC_CLASS}>{description}</div>}
    </label>
    <div className="flex-1">{children}</div>
  </div>
);

export interface FormGenieConfigurationProps {
  basePath?: string;
}

export function FormGenieConfiguration({ basePath = '/formgenie' }: FormGenieConfigurationProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const token = useTypedSelector((state) => state.auth.token);
  const { mode } = useParams<{ mode: string }>();

  const [createAiWorkers, { isLoading: isCreating }] = useCreateAiWorkersMutation();
  const [updateAiWorkers, { isLoading: isUpdating }] = useUpdateAiWorkersMutation();

  const { formGenieWorker, worker } = (location?.state ?? {}) as any;
  const workerFromState = formGenieWorker || worker;
  const isCreateMode = mode === RouteState.CREATE;

  const [workerName, setWorkerName] = useState('');
  const [publicDescription, setPublicDescription] = useState('');
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [selectedCredentialId, setSelectedCredentialId] = useState('');

  const { data: workflowsResponse, isLoading: isLoadingWorkflows } = useGetDifyWorkflowsQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
  const workflowsData = workflowsResponse || [];
  const { data: credentialsList, isLoading: isCredentialsListLoading } = useGetCredentialsListQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
  const credentialOptions = Array.isArray(credentialsList) ? credentialsList : [];

  useEffect(() => {
    if (workerFromState) {
      const result: Record<string, any> = {};
      (workerFromState.aiworkers_attrs ?? []).forEach((item: any) => {
        result[item?.attr_name] = item.attr_json !== null ? item.attr_json : item.attr_value;
      });
      setWorkerName(workerFromState.name ?? '');
      setPublicDescription(workerFromState.description ?? '');
      if (result?.workflow_id) setSelectedWorkflowId(result.workflow_id);
      if (result?.credential_id) setSelectedCredentialId(result.credential_id);
    }
  }, [workerFromState]);

  const handleSave = async () => {
    if (!workerName.trim()) {
      CtyShowToast(checkType.error, t('Worker name is required'));
      return;
    }
    if (!selectedWorkflowId) {
      CtyShowToast(checkType.error, t('Please select a workflow'));
      return;
    }
    if (!selectedCredentialId) {
      CtyShowToast(checkType.error, t('Please select a credential'));
      return;
    }

    const aiworkersAttrs: any[] = [
      { attr_name: 'workflow_id', attr_value: selectedWorkflowId, attr_json: null },
    ];
    const selectedWorkflow = (workflowsData as any[]).find((w: any) => w.workflow_id === selectedWorkflowId);
    if (selectedWorkflow?.mode != null) {
      aiworkersAttrs.push({ attr_name: 'workflow_type', attr_value: String(selectedWorkflow.mode), attr_json: null });
    }
    aiworkersAttrs.push({ attr_name: 'credential_id', attr_value: selectedCredentialId, attr_json: null });

    const payload = { name: workerName, description: publicDescription, aiworkers_attrs: aiworkersAttrs };

    try {
      if (isCreateMode) {
        const response = await createAiWorkers({ category: 'formgenie' as any, type: 'product', ...payload }).unwrap();
        CtyShowToast(checkType.success, t('FormGenie worker created successfully'));
        navigate(`${basePath}/${response.data.aiworker_id}?tab=configuration`, {
          state: { formGenieWorker: response.data },
        });
      } else {
        if (!workerFromState?.aiworker_id) {
          CtyShowToast(checkType.error, t('Worker ID not found'));
          return;
        }
        const response = await updateAiWorkers({ payLoad: payload, aiworker_id: workerFromState.aiworker_id }).unwrap();
        navigate(`${location.pathname}${location.search}`, {
          state: { formGenieWorker: response?.data || workerFromState },
        });
        CtyShowToast(checkType.success, t('FormGenie worker updated successfully'));
      }
    } catch (error: any) {
      CtyShowToast(checkType.error, error?.data?.message || t('Failed to save FormGenie worker'));
    }
  };

  return (
    <div className="flex flex-col h-full gap-3 pt-2 pb-3">
      <div className="flex-1 overflow-y-auto w-full px-5">
        <div className="flex flex-col gap-3">
          <div className="md:w-[70%] flex flex-col gap-3">
            <FormRow label={t('Worker name')} htmlFor="worker_name" required>
              <Input
                id="worker_name"
                type="text"
                size="sm"
                aria-label="worker_name"
                value={workerName}
                isRequired
                onValueChange={setWorkerName}
              />
            </FormRow>

            {!isCreateMode && (
              <FormRow label={t('Worker ID')} htmlFor="worker_id">
                <Input
                  id="worker_id"
                  type="text"
                  size="sm"
                  aria-label="worker_id"
                  isReadOnly
                  value={workerFromState?.aiworker_id ?? ''}
                />
              </FormRow>
            )}

            <FormRow label={t('Description')} htmlFor="public_description" required description={t('Describe what kinds of forms this worker generates')}>
              <Textarea
                id="public_description"
                minRows={5}
                maxRows={7}
                isRequired
                aria-label="public_description"
                value={publicDescription}
                onValueChange={setPublicDescription}
              />
            </FormRow>
          </div>

          <div className="md:w-[70%] flex flex-col gap-3">
            <FormRow label={t('Select Workflow')} required description={t('Choose the AI workflow that powers form generation')}>
              <Select
                id="workflow-select"
                size="sm"
                aria-label="Select Workflow"
                placeholder={isLoadingWorkflows ? t('Loading workflows...') : t('Select a workflow')}
                selectedKeys={selectedWorkflowId ? new Set([selectedWorkflowId]) : new Set()}
                onSelectionChange={(keys) => setSelectedWorkflowId(String([...keys][0]))}
                isLoading={isLoadingWorkflows}
                isDisabled={isLoadingWorkflows}
              >
                {(workflowsData as any[]).map((workflow: any) => (
                  <SelectItem key={workflow.workflow_id} value={workflow.workflow_id}>
                    {workflow.workflow_name}
                  </SelectItem>
                ))}
              </Select>
            </FormRow>

            <FormRow label={t('Select Credential')} required description={t('Choose a credential for the AI workflow')}>
              <Select
                id="credential-select"
                size="sm"
                aria-label="Select Credential"
                placeholder={isCredentialsListLoading ? t('Loading credentials...') : t('Select a credential')}
                selectedKeys={selectedCredentialId ? new Set([selectedCredentialId]) : new Set()}
                onSelectionChange={(keys) => setSelectedCredentialId(String([...keys][0] ?? ''))}
                isLoading={isCredentialsListLoading}
                isDisabled={isCredentialsListLoading}
              >
                {credentialOptions.map((credential: any) => (
                  <SelectItem key={credential.credential_id} value={credential.credential_id}>
                    {credential.name}
                  </SelectItem>
                ))}
              </Select>
            </FormRow>
          </div>

          <div className="flex justify-end md:w-[70%]">
            <CtyButton
              variant="solid"
              color="primary"
              size="md"
              radius="sm"
              onPress={handleSave}
              isLoading={isCreating || isUpdating}
            >
              {isCreateMode ? t('Save') : t('Update')}
            </CtyButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FormGenieConfiguration;
